import { type CompanyFormat, formatMoney, TextButton } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { useQueries, useQuery } from "@tanstack/react-query";
import { MapPin, MapPinOff, Minus, Plus, X } from "lucide-react";
import { useState } from "react";
import { errorOf } from "../../api";
import { messageFor } from "../../error-copy";
import { RouterLink } from "../../router-link";
import {
  breakdown,
  cellOf,
  distanceText,
  type LookedAt,
  type NearZone,
  placeQuote,
  type ZoneSheet,
  zoneAtQuery,
} from "../../zones";
import { CellPrice, lineAmount, PriceBreakdown, Source, ZonePriceList } from "./prices";
import styles from "./zones.module.css";

type Money = Pick<CompanyFormat, "currency">;

/** "Saved place", "Address" or "Dropped pin": what kind of place is being looked at. */
export const kindText = (place: LookedAt) =>
  place.kind === "saved" ? "Saved place" : place.kind === "address" ? "Address" : "Dropped pin";

/**
 * A place on the zones page (P8-40, `Console / Service zones / Place priced` and `… / Place
 * outside zones`): its zone, what each item costs there, and a quote with costs and margin. A
 * saved place is priced as itself, contracts included; an address or a pin as a place in its zone
 * with no owner.
 */
export function PlacePanel({
  place,
  sheet,
  itemId,
  format,
  canEdit,
  onClose,
  onOpenZone,
  onNewZone,
}: {
  place: LookedAt;
  /** null: the staff member can't see prices (orders.read) */
  sheet: ZoneSheet | null;
  itemId: string | undefined;
  format: Money;
  canEdit: boolean;
  onClose: () => void;
  onOpenZone: (id: string) => void;
  onNewZone: () => void;
}) {
  const at = useQuery(zoneAtQuery(place.lng, place.lat));
  const zone = at.data?.geofence ?? null;
  return (
    <section className={styles.card} aria-label={place.label}>
      <div className={styles.placeHead}>
        <span className={zone || at.isPending ? styles.placeGlyph : styles.placeGlyphDanger} aria-hidden>
          {zone || at.isPending ? <MapPin size={18} /> : <MapPinOff size={18} />}
        </span>
        <div className={styles.stack}>
          <h2 className={styles.title}>{place.label}</h2>
          <span className={styles.mutedInline}>{[kindText(place), place.detail].filter(Boolean).join(" · ")}</span>
          {place.kind === "saved" && place.href ? (
            <RouterLink href={place.href} className={styles.link}>
              Open the owner's places
            </RouterLink>
          ) : null}
        </div>
        <button type="button" className={styles.iconButton} aria-label="Close" onClick={onClose}>
          <X size={16} aria-hidden />
        </button>
      </div>
      {at.isPending ? <p className={styles.muted}>Finding its zone…</p> : null}
      {at.error ? (
        <Alert tone="danger" title="Couldn't find its zone">
          {messageFor(errorOf(at.error))}
        </Alert>
      ) : null}
      {at.data && zone ? (
        <InZone
          place={place}
          zone={{ id: zone.id, name: zone.name }}
          sheet={sheet}
          itemId={itemId}
          format={format}
          onOpenZone={onOpenZone}
        />
      ) : null}
      {at.data && !zone ? (
        <Outside
          nearest={at.data.nearest}
          sheet={sheet}
          itemId={itemId}
          format={format}
          canEdit={canEdit}
          onOpenZone={onOpenZone}
          onNewZone={onNewZone}
        />
      ) : null}
    </section>
  );
}

function InZone({
  place,
  zone,
  sheet,
  itemId,
  format,
  onOpenZone,
}: {
  place: LookedAt;
  zone: { id: string; name: string };
  sheet: ZoneSheet | null;
  itemId: string | undefined;
  format: Money;
  onOpenZone: (id: string) => void;
}) {
  return (
    <>
      <div className={styles.zoneRow}>
        <span>
          In <strong>{zone.name}</strong>
        </span>
        <span className={styles.mutedInline}>the smallest zone covering it</span>
      </div>
      {sheet ? (
        <>
          <h3 className={styles.subtitle}>Prices at this place</h3>
          {place.kind === "saved" ? (
            <SavedPrices locationId={place.id} sheet={sheet} zoneName={zone.name} format={format} />
          ) : (
            <>
              <ZonePriceList sheet={sheet} zoneId={zone.id} zoneName={zone.name} format={format} />
              <p className={styles.muted}>Without a customer: contract prices apply to saved places only.</p>
            </>
          )}
          {itemId ? (
            <PlaceQuote
              at={place.kind === "saved" ? { location_id: place.id } : { geofence_id: zone.id }}
              itemId={itemId}
              itemName={sheet.items.find((i) => i.id === itemId)?.name ?? "Item"}
              zoneName={zone.name}
              format={format}
            />
          ) : null}
        </>
      ) : (
        <p className={styles.muted}>Prices need the orders.read permission.</p>
      )}
      <div className={styles.actions}>
        <TextButton onClick={() => onOpenZone(zone.id)}>Open {zone.name}</TextButton>
      </div>
    </>
  );
}

/** A saved place's own price for each item: its contract, or its zone's, or any zone's. */
function SavedPrices({
  locationId,
  sheet,
  zoneName,
  format,
}: {
  locationId: string;
  sheet: ZoneSheet;
  zoneName: string;
  format: Money;
}) {
  const quotes = useQueries({
    queries: sheet.items.map((item) => ({
      queryKey: ["zones", "place-price", locationId, item.id],
      queryFn: () => placeQuote({ location_id: locationId }, item.id, 1),
      retry: false,
    })),
  });
  return (
    <ul className={styles.priceList} aria-label="Prices at this place">
      {sheet.items.map((item, i) => {
        const q = quotes[i];
        const price = q.data ? breakdown(q.data.deliveries.flatMap((d) => d.lines)).price : null;
        const missing = q.error && errorOf(q.error).code === "MISSING_PRICE";
        return (
          <li key={item.id} className={styles.priceRow}>
            <span>{item.name}</span>
            {price ? <Source scope={price.scope} zoneName={zoneName} /> : <span />}
            <span className={styles.amount}>
              {q.isPending ? (
                "…"
              ) : price ? (
                lineAmount(price, format)
              ) : missing ? (
                <span className={styles.warningText}>No price</span>
              ) : q.error ? (
                <span className={styles.warningText}>{messageFor(errorOf(q.error))}</span>
              ) : (
                "—"
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** A quote for a number of the chosen item: one delivery's breakdown, then the whole. */
function PlaceQuote({
  at,
  itemId,
  itemName,
  zoneName,
  format,
}: {
  at: { location_id: string } | { geofence_id: string };
  itemId: string;
  itemName: string;
  zoneName: string;
  format: Money;
}) {
  const [quantity, setQuantity] = useState(1);
  const quote = useQuery({
    queryKey: ["zones", "place-quote", at, itemId, quantity],
    queryFn: () => placeQuote(at, itemId, quantity),
    retry: false,
  });
  const first = quote.data?.deliveries[0];
  const several = (quote.data?.deliveries.length ?? 0) > 1;
  return (
    <section className={styles.quote} aria-label="Quote">
      <div className={styles.quoteHead}>
        <h3 className={styles.subtitle}>Quote</h3>
        <div className={styles.stepper}>
          <button
            type="button"
            aria-label="One less"
            disabled={quantity <= 1}
            onClick={() => setQuantity(quantity - 1)}
          >
            <Minus size={12} aria-hidden />
          </button>
          <span>
            {quantity} × {itemName}
          </span>
          <button
            type="button"
            aria-label="One more"
            disabled={quantity >= 50}
            onClick={() => setQuantity(quantity + 1)}
          >
            <Plus size={12} aria-hidden />
          </button>
        </div>
      </div>
      {quote.isPending ? <p className={styles.muted}>Pricing…</p> : null}
      {quote.error ? <p className={styles.warningText}>{messageFor(errorOf(quote.error))}</p> : null}
      {first ? (
        <>
          {several ? <p className={styles.muted}>Each delivery:</p> : null}
          <PriceBreakdown
            lines={first.lines}
            totals={{ receivable: first.receivable_total, payable: first.payable_total, margin: first.margin }}
            zoneName={zoneName}
            format={format}
          />
        </>
      ) : null}
      {quote.data && several ? (
        <p className={styles.quoteTotal}>
          {quote.data.deliveries.length} deliveries: the customer pays{" "}
          <strong>{formatMoney(quote.data.receivable_total, format)}</strong>, margin{" "}
          {formatMoney(quote.data.margin, format)}
        </p>
      ) : null}
    </section>
  );
}

/** Outside every zone: it can't be priced; the nearest zones, and drawing one here. */
function Outside({
  nearest,
  sheet,
  itemId,
  format,
  canEdit,
  onOpenZone,
  onNewZone,
}: {
  nearest: NearZone[];
  sheet: ZoneSheet | null;
  itemId: string | undefined;
  format: Money;
  canEdit: boolean;
  onOpenZone: (id: string) => void;
  onNewZone: () => void;
}) {
  return (
    <>
      <Alert tone="danger" title="Outside every zone">
        No price applies here, so it can't be quoted or ordered to.
      </Alert>
      {nearest.length ? (
        <>
          <h3 className={styles.subtitle}>Nearest zones</h3>
          <ul className={styles.priceList} aria-label="Nearest zones">
            {nearest.map((z) => (
              <li key={z.id} className={styles.priceRow}>
                <span className={styles.stack}>
                  <TextButton onClick={() => onOpenZone(z.id)}>{z.name}</TextButton>
                  <span className={styles.mutedInline}>
                    {distanceText(z.distance_m)} away{z.status === "inactive" ? " · switched off" : ""}
                  </span>
                </span>
                <span />
                <span className={styles.amount}>
                  {sheet ? <CellPrice cell={cellOf(sheet, z.id, itemId)} zoneName={z.name} format={format} /> : null}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <p className={styles.muted}>
        Draw a zone that covers it, or widen the nearest one. Saving matches every place again, and shows who moved.
      </p>
      {canEdit ? (
        <div className={styles.actions}>
          <TextButton onClick={onNewZone}>New zone here</TextButton>
        </div>
      ) : null}
    </>
  );
}
